import { skipToken } from '@reduxjs/toolkit/query/react';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import {
  convertBackendToFrontend,
  convertFrontendToBackend,
  type IntakeSummaryFrontend,
  mapBackendFormStatusesToFrontend,
  mapBackendIntakeStatusToFrontend,
  summaryBackendToFrontend,
  summaryFrontendToBackend,
} from '../components/intakes/form/formatter';
import { INITIAL_FORM } from '../components/intakes/form/initialForm';
import {
  GeneralNoteEntry,
  IntakeFormData,
} from '../components/intakes/form/types';
import { sanitizeIntakeFormData } from '../components/intakes/utils/formDataSanitizers';
import {
  useGetIntakeQuery,
  useUpdateIntakeMutation,
  useUpdateIntakeNotesLogMutation,
} from '../services/api/intakeService';
import { IntakeHandler, IntakeHandlerValue } from '../services/types/intake-types';
import { selectUser } from '../state/selectors/root-selectors';

// Only sections that are objects – exclude primitive/array fields
type ObjectSectionKey = Exclude<
  keyof IntakeFormData,
  'generalNotes' | 'generalNotesLog' | 'generalNotesLegacy' | 'companions'
>;

// Types for intake status tracking

export interface IntakeStatuses {
  loeStatus:
    | 'Not Sent'
    | 'Sent'
    | 'Signed'
    | 'Sent (Manual)'
    | 'Signed (Manual)';
  attorneyDecision:
    | 'Not Reviewed'
    | 'Sent for Review'
    | 'Attorney Approved'
    | 'Finch Rejection'
    | 'Attorney Rejection';
  rejectionReason: string;
  hipaaStatus: 'Not Requested' | 'Requested' | 'Received';
  declarationStatus: 'Not Received' | 'Received';
  insuranceStatus: 'Not Received' | 'Received';
}

export const useIntakeForm = (intakeId: string | null, isNewIntake = false) => {
  // Get current logged-in user for note attribution
  const currentUser = useSelector(selectUser);

  // API hooks
  const {
    data: intakeData,
    isLoading,
    error,
    refetch,
  } = useGetIntakeQuery(intakeId && !isNewIntake ? intakeId : skipToken, {
    skip: !intakeId || isNewIntake,
  });
  const [updateIntake, { isLoading: isSaving }] = useUpdateIntakeMutation();
  const [updateNotesLog, { isLoading: isUpdatingNotes }] =
    useUpdateIntakeNotesLogMutation();

  // Local state
  const [form, setForm] = useState<IntakeFormData>(INITIAL_FORM);
  const [status, setStatus] = useState<
    | 'In Progress'
    | 'Chase'
    | 'Sent for Client Signature'
    | 'Signed'
    | 'Archived'
    | 'Rejected'
    | 'Lost'
    | 'Escalated'
  >('In Progress');
  const [matterId, setMatterId] = useState<string | null>(null);
  const [matterStatus, setMatterStatus] = useState<string | null>(null);
  const [firmId, setFirmId] = useState<string | null>(null);
  const [selectedFirm, setSelectedFirm] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [creatorName, setCreatorName] = useState<string>('');
  const [assignedTo, setAssignedTo] = useState<string | null>(null);
  const [assignedToName, setAssignedToName] = useState<string>('');
  const [companionRoot, setCompanionRoot] = useState<string | null>(null);
  const [isCompanionPrimary, setIsCompanionPrimary] = useState<boolean>(true);
  const [intakeStatuses, setIntakeStatuses] = useState<IntakeStatuses>({
    loeStatus: 'Not Sent',
    attorneyDecision: 'Not Reviewed',
    rejectionReason: '',
    hipaaStatus: 'Not Requested',
    declarationStatus: 'Not Received',
    insuranceStatus: 'Not Received',
  });
  const [handler, setHandler] = useState<IntakeHandlerValue>(
    IntakeHandler.FINCH,
  );
  const [summary, setSummary] = useState<IntakeSummaryFrontend>({
    caseSummary: '',
    greenFlags: '',
    yellowFlags: '',
    redFlags: '',
    recommendationDecision: '',
    recommendationReason: '',
    recommendationCounterpointHeading: null,
    recommendationCounterpoint: null,
  });

  // Track if form has been hydrated from server data
  // This prevents autosave from treating initial data load as "unsaved changes"
  const [isFormHydrated, setIsFormHydrated] = useState(isNewIntake);

  // Load data when available
  useEffect(() => {
    if (!intakeData || isNewIntake) {
      return;
    }

    try {
      // Persist firm relationship
      if (intakeData.firm) {
        setFirmId(intakeData.firm);
      }

      setCreatorName(intakeData.creator_name || '');
      setAssignedTo(intakeData.assigned_to);
      setAssignedToName(intakeData.assigned_to_name || '');

      // Set selectedFirm directly from intakeData (firm ID + name)
      if (intakeData.firm && intakeData.firm_name) {
        setSelectedFirm({ id: intakeData.firm, name: intakeData.firm_name });
      }

      if (intakeData.matter_id) {
        setMatterId(intakeData.matter_id);
      }
      setMatterStatus(intakeData.matter_status ?? null);

      setCompanionRoot(intakeData.companion_root ?? null);
      setIsCompanionPrimary(intakeData.is_companion_primary ?? true);

      if (!intakeData.form_data) {
        return;
      }

      // Convert backend data to frontend format
      const frontendFormData = convertBackendToFrontend(intakeData.form_data);
      setForm((prevForm) => {
        // Preserve the local generalNotes draft to prevent cursor jump during
        // auto-save refetch.  When the structured notes log hasn't changed we
        // know the refetch is just echoing back our own save, so the user's
        // current textarea contents should win over the (potentially stale)
        // server value.  Only replace notes when the log changes (i.e. someone
        // added a structured note via "Save Note").
        if (prevForm) {
          const prevLog = JSON.stringify(prevForm.generalNotesLog || []);
          const nextLog = JSON.stringify(
            frontendFormData.generalNotesLog || [],
          );

          if (prevForm.generalNotes && prevLog === nextLog) {
            return { ...frontendFormData, generalNotes: prevForm.generalNotes };
          }
        }

        return frontendFormData;
      });

      // Load summary (snake_case) into camel state
      setSummary(summaryBackendToFrontend(intakeData.summary));

      // Map backend status values to frontend types
      setIntakeStatuses(mapBackendFormStatusesToFrontend(intakeData.form_data));

      // Map main status from backend to frontend
      setStatus(
        mapBackendIntakeStatusToFrontend(
          intakeData.status as Parameters<
            typeof mapBackendIntakeStatusToFrontend
          >[0],
        ),
      );

      // Set handler from backend data
      if (intakeData.handler) {
        setHandler(intakeData.handler as IntakeHandlerValue);
      }

      // Mark form as hydrated after all data is loaded
      setIsFormHydrated(true);
    } catch (loadError) {
      toast.error('Failed to load intake data');
    }
  }, [intakeData, isNewIntake]);

  // Handle field changes - supports nested objects
  const handleFieldChange = useCallback(
    (
      section: ObjectSectionKey,
      field: string,
      value:
        | string
        | number
        | boolean
        | null
        | undefined
        | Record<string, unknown>,
    ) => {
      setForm((prev: IntakeFormData) => {
        // Handle nested object updates
        if (field.includes('.')) {
          const [parentField, childField] = field.split('.');
          const sectionData = prev[section] as Record<string, unknown>;
          const parent = (sectionData[parentField] || {}) as Record<
            string,
            unknown
          >;
          return {
            ...prev,
            [section]: {
              ...sectionData,
              [parentField]: {
                ...parent,
                [childField]: value,
              },
            },
          };
        }

        // Handle regular field updates
        return {
          ...prev,
          [section]: {
            ...(prev[section] as Record<string, unknown>),
            [field]: value,
          },
        };
      });
    },
    [],
  ); // No dependencies needed since we use functional setState

  const handleCompanionsChange = useCallback(
    (companions: IntakeFormData['companions']) => {
      setForm((prev: IntakeFormData) => ({ ...prev, companions }));
    },
    [],
  );

  // Handle status changes
  const handleStatusChange = (newStatuses: IntakeStatuses) => {
    setIntakeStatuses(newStatuses);
  };

  // Handle firm change from Combobox
  const handleFirmChange = useCallback(
    (firm: { id: string; name: string } | null) => {
      setSelectedFirm(firm);
      setFirmId(firm?.id || null);
    },
    [],
  );

  // Update evidence statuses as a batch (for review dialog)
  const handleEvidenceStatusesChange = useCallback(
    (value: IntakeFormData['evidenceStatuses']) => {
      // Persist only notes to backend; statuses are derived at runtime in the dialog.
      setForm((prev) => ({ ...prev, evidenceStatuses: value }));
    },
    [],
  );

  // Handle general notes change
  const handleGeneralNotesChange = useCallback((value: string) => {
    setForm((prev: IntakeFormData) => {
      const newForm = { ...prev };
      newForm.generalNotes = value;
      return newForm;
    });
  }, []);

  // Handle lead source change
  const handleLeadSourceChange = useCallback((value: string) => {
    setForm((prev: IntakeFormData) => ({
      ...prev,
      leadSource: value,
    }));
  }, []);

  // Append a new note into the structured log and persist
  const addGeneralNote = useCallback(
    async (text: string) => {
      if (!intakeId) return;
      const trimmed = (text || '').trim();
      if (!trimmed) return;

      const entry: GeneralNoteEntry = {
        at: new Date().toISOString(),
        text: trimmed,
        // Use current logged-in user as author, not the assigned user
        authorId: currentUser?.id,
        authorName: currentUser
          ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() ||
            currentUser.email
          : undefined,
      };

      // Derive the next log once to avoid double-appending issues
      const nextLogFrontend = [...(form.generalNotesLog || []), entry];

      // Optimistically update local state to mirror what we will send
      setForm((prev: IntakeFormData) => ({
        ...prev,
        generalNotes: '',
        generalNotesLog: nextLogFrontend,
      }));

      try {
        const nextLog = nextLogFrontend.map((n: GeneralNoteEntry) => ({
          at: n.at,
          text: n.text,
          author_id: n.authorId,
          author_name: n.authorName,
        }));
        await updateNotesLog({
          id: intakeId,
          general_notes_log: nextLog,
        }).unwrap();
      } catch (e) {
        toast.error('Failed to add note. Please try again.');
        // Roll back state by refetching
        try {
          await refetch();
        } catch (_refetchError) {
          // no-op; additional failure will be surfaced elsewhere
        }
        throw e as Error;
      }
    },
    [intakeId, currentUser, updateNotesLog, form, refetch],
  );

  // Handle assigned_to change
  const handleAssignedToChange = useCallback((userId: string | null) => {
    setAssignedTo(userId);
  }, []);

  // Handle manual save (with optimistic updates and success toast)
  const handleSave = async () => {
    if (!intakeId) {
      const err = new Error('No intake ID available for saving');
      toast.error(err.message);
      throw err;
    }

    try {
      // Refetch latest data from server before saving to pick up any changes
      // made by the ScreeningSheet auto-save (which uses skipOptimistic and
      // doesn't invalidate the IntakeForm's query cache).
      const freshData = await refetch().unwrap();
      const freshFormData = freshData?.form_data;

      // Convert ALL frontend data to backend format in one place
      const backendFormData = convertFrontendToBackend(form, intakeStatuses);

      // Merge with fresh server data to avoid overwriting screening-saved
      // header values (loss date, state, case type, client info) with stale
      // local form state. Only override server values with non-empty local values
      // so empty defaults from the IntakeForm don't wipe screening-saved data.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nonEmpty = (obj: any) =>
        Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v != null));

      const formDataWithScreening = {
        ...backendFormData,
        incident_info: {
          ...freshFormData?.incident_info,
          ...nonEmpty(backendFormData.incident_info || {}),
        } as typeof backendFormData.incident_info,
        personal_info: {
          ...freshFormData?.personal_info,
          ...nonEmpty(backendFormData.personal_info || {}),
        } as typeof backendFormData.personal_info,
        // Preserve screening data (managed by ScreeningSheet separately)
        ...(freshFormData?.screening && { screening: freshFormData.screening }),
        ...(freshFormData?.screening_v2 && { screening_v2: freshFormData.screening_v2 }),
      };

      await updateIntake({
        id: intakeId,
        form_data: sanitizeIntakeFormData(formDataWithScreening),
        summary: summaryFrontendToBackend(summary),
        firm: selectedFirm?.id,
        assigned_to: assignedTo,
        manual_qualification: intakeData?.manual_qualification,
        rejection_email_sent: intakeData?.rejection_email_sent,
        skipOptimistic: false, // Manual save: enable optimistic updates
      }).unwrap();

      toast.success('Intake saved successfully!');
    } catch (saveError) {
      toast.error('Failed to save intake. Please try again.');
      throw saveError as Error;
    }
  };

  // Handle auto-save (silent, no optimistic updates)
  const handleAutoSave = async () => {
    if (!intakeId) {
      // Silently skip if no ID (shouldn't happen)
      return;
    }

    try {
      // Convert ALL frontend data to backend format in one place
      const backendFormData = convertFrontendToBackend(form, intakeStatuses);

      // Preserve screening data and merge shared fields with server state
      // to avoid overwriting screening-saved header values with stale form state.
      // Only override server values with non-empty local values.
      const existingFormData = intakeData?.form_data;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nonEmpty = (obj: any) =>
        Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== '' && v != null));

      const formDataWithScreening = {
        ...backendFormData,
        incident_info: {
          ...existingFormData?.incident_info,
          ...nonEmpty(backendFormData.incident_info || {}),
        } as typeof backendFormData.incident_info,
        personal_info: {
          ...existingFormData?.personal_info,
          ...nonEmpty(backendFormData.personal_info || {}),
        } as typeof backendFormData.personal_info,
        ...(existingFormData?.screening && { screening: existingFormData.screening }),
        ...(existingFormData?.screening_v2 && { screening_v2: existingFormData.screening_v2 }),
      };

      await updateIntake({
        id: intakeId,
        form_data: sanitizeIntakeFormData(formDataWithScreening),
        summary: summaryFrontendToBackend(summary),
        firm: selectedFirm?.id,
        assigned_to: assignedTo,
        manual_qualification: intakeData?.manual_qualification,
        rejection_email_sent: intakeData?.rejection_email_sent,
        skipOptimistic: true, // Auto-save: skip optimistic updates
      }).unwrap();

      // No toast for auto-save (silent operation)
    } catch (saveError) {
      toast.error('Auto-save failed. Please retry or save manually.');
      throw saveError as Error; // Still throw so the hook can track the error
    }
  };

  return {
    // Data
    form,
    status,
    handler,
    matterId,
    matterStatus,
    firmId,
    selectedFirm,
    intakeStatuses,
    creatorName,
    assignedTo,
    assignedToName,
    companionRoot,
    isCompanionPrimary,
    summary,
    setSummary,
    handleEvidenceStatusesChange,
    manualQualification: intakeData?.manual_qualification ?? null,
    rejectionEmailSent: intakeData?.rejection_email_sent ?? false,
    isScheduled: intakeData?.is_scheduled ?? false,
    scheduledAt: intakeData?.scheduled_at ?? null,
    scheduledAssigneeId: intakeData?.scheduled_assignee ?? null,
    scheduledAssigneeName: intakeData?.scheduled_assignee_name ?? null,
    scheduledBackupAssigneeId: intakeData?.scheduled_backup_assignee ?? null,
    scheduledBackupAssigneeName: intakeData?.scheduled_backup_assignee_name ?? null,
    googleCalendarEventId: intakeData?.google_calendar_event_id ?? null,
    googleCalendarEventLink: intakeData?.google_calendar_event_link ?? null,
    hasLinkedLead: (intakeData as Record<string, unknown> | undefined)?.has_linked_lead === true,
    linkedLeadStatus: ((intakeData as Record<string, unknown> | undefined)?.linked_lead_status as string) ?? null,
    // Loading states
    isLoading,
    isSaving,
    isUpdatingNotes,
    isFormHydrated,
    error,

    // Actions
    handleFieldChange,
    handleStatusChange,
    handleSave,
    handleAutoSave,
    handleFirmChange,
    handleGeneralNotesChange,
    handleLeadSourceChange,
    handleAssignedToChange,
    addGeneralNote,
    handleCompanionsChange,
    setStatus,
    refetch,
  };
};
